import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Sparkles,
  UserCheck,
  GitFork,
  FileCheck,
  Save,
  ArrowLeft,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const STEP_TYPES = [
  { value: 'ai_extraction', label: 'AI extraction', icon: Sparkles, description: 'Run a skill to extract structured data' },
  { value: 'ai_validation', label: 'AI validation', icon: FileCheck, description: 'Second-pass validation or coding' },
  { value: 'cross_reference', label: 'Cross-reference', icon: GitFork, description: 'Compare claims across documents' },
  { value: 'human_review', label: 'Human review', icon: UserCheck, description: 'Pause for reviewer approval' },
  { value: 'output_generation', label: 'Output', icon: FileCheck, description: 'Produce a final artefact' },
] as const;

const WORKFLOW_TYPES = [
  { value: 'regulatory_review', label: 'Regulatory review' },
  { value: 'clinical_extraction', label: 'Clinical extraction' },
  { value: 'pv_intake', label: 'Pharmacovigilance intake' },
  { value: 'compliance_scan', label: 'Compliance scan' },
  { value: 'gap_analysis', label: 'Gap analysis' },
  { value: 'custom', label: 'Custom' },
] as const;

interface StepDraft {
  id: string;
  name: string;
  type: (typeof STEP_TYPES)[number]['value'];
  skill: string;
  requiredApproval: boolean;
  riskLevel: 'low' | 'medium' | 'high';
}

export function WorkflowDesignerPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const skills = useQuery({
    queryKey: ['skills'],
    queryFn: async () => (await api.get('/api/admin/skills')).data,
    // Skills admin endpoint may not exist — fall back to a static list:
    retry: false,
  });
  const SKILL_OPTIONS = (skills.data?.items as Array<{ name: string }> | undefined)?.map((s) => s.name) ?? [
    'regulatory-csr-extraction',
    'cmc-analysis',
    'cross-reference-check',
    'gap-analysis',
    'meddra-coding',
    'safety-narrative',
    'psmf-scan',
    'sop-scan',
  ];

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<(typeof WORKFLOW_TYPES)[number]['value']>('regulatory_review');
  const [steps, setSteps] = useState<StepDraft[]>([
    {
      id: crypto.randomUUID(),
      name: 'Extract structured data',
      type: 'ai_extraction',
      skill: 'regulatory-csr-extraction',
      requiredApproval: false,
      riskLevel: 'medium',
    },
    {
      id: crypto.randomUUID(),
      name: 'Reviewer approval',
      type: 'human_review',
      skill: '',
      requiredApproval: true,
      riskLevel: 'high',
    },
  ]);

  function addStep() {
    setSteps((s) => [
      ...s,
      {
        id: crypto.randomUUID(),
        name: `Step ${s.length + 1}`,
        type: 'ai_extraction',
        skill: 'regulatory-csr-extraction',
        requiredApproval: false,
        riskLevel: 'medium',
      },
    ]);
  }

  function updateStep(id: string, patch: Partial<StepDraft>) {
    setSteps((s) => s.map((st) => (st.id === id ? { ...st, ...patch } : st)));
  }

  function removeStep(id: string) {
    setSteps((s) => s.filter((st) => st.id !== id));
  }

  function moveStep(id: string, dir: -1 | 1) {
    setSteps((s) => {
      const idx = s.findIndex((x) => x.id === id);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= s.length) return s;
      const next = [...s];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }

  const create = useMutation({
    mutationFn: async () => {
      if (steps.length === 0) throw new Error('At least one step is required');
      if (!name.trim()) throw new Error('Name is required');

      // Link steps sequentially via `next` and `onFailure`
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        definition: {
          startStep: steps[0].name,
          humanCheckpoints: steps.filter((s) => s.type === 'human_review').map((s) => s.name),
          steps: steps.map((s, i) => ({
            name: s.name,
            type: s.type,
            skill: s.type === 'ai_extraction' || s.type === 'ai_validation' ? s.skill : undefined,
            requiredApproval: s.requiredApproval,
            riskLevel: s.riskLevel,
            next: i < steps.length - 1 ? steps[i + 1].name : undefined,
          })),
        },
      };
      return (await api.post('/api/workflows', body)).data;
    },
    onSuccess: (wf) => {
      toast.success('Workflow created', `"${wf.name}" is ready to run against documents`);
      qc.invalidateQueries({ queryKey: ['workflows'] });
      navigate('/workflows');
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/workflows">
            <ArrowLeft className="mr-2 h-4 w-4" /> Workflows
          </Link>
        </Button>
        <h1 className="font-display text-2xl">Design a workflow</h1>
        <p className="text-sm text-muted-foreground">
          Chain AI skills with optional human checkpoints. Every transition is audited and
          e-signature-protected.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="grid gap-4 lg:grid-cols-[1fr,360px]">
        {/* Steps */}
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Workflow steps</CardTitle>
              <CardDescription>Executed top-down. Drag-free reorder via arrows.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {steps.map((step, idx) => {
                const StepIcon = STEP_TYPES.find((t) => t.value === step.type)?.icon ?? Sparkles;
                return (
                  <div
                    key={step.id}
                    className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/40"
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                          {idx + 1}
                        </div>
                        <StepIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{step.name}</span>
                        {step.requiredApproval && (
                          <Badge variant="secondary" className="text-[10px]">
                            Approval required
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveStep(step.id, -1)}
                          disabled={idx === 0}
                          className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStep(step.id, 1)}
                          disabled={idx === steps.length - 1}
                          className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeStep(step.id)}
                          className="rounded p-1 text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-[10px]">Step name</Label>
                        <Input
                          value={step.name}
                          onChange={(e) => updateStep(step.id, { name: e.target.value })}
                          className="h-8 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Step type</Label>
                        <select
                          value={step.type}
                          onChange={(e) => updateStep(step.id, { type: e.target.value as StepDraft['type'] })}
                          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                        >
                          {STEP_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      {(step.type === 'ai_extraction' || step.type === 'ai_validation') && (
                        <div className="sm:col-span-2">
                          <Label className="text-[10px]">Skill</Label>
                          <select
                            value={step.skill}
                            onChange={(e) => updateStep(step.id, { skill: e.target.value })}
                            className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                          >
                            {SKILL_OPTIONS.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <Label className="text-[10px]">Risk level</Label>
                        <select
                          value={step.riskLevel}
                          onChange={(e) => updateStep(step.id, { riskLevel: e.target.value as StepDraft['riskLevel'] })}
                          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={step.requiredApproval}
                          onChange={(e) => updateStep(step.id, { requiredApproval: e.target.checked })}
                          className="h-4 w-4 accent-primary"
                        />
                        Require e-signature approval
                      </label>
                    </div>
                  </div>
                );
              })}
              <Button type="button" variant="outline" onClick={addStep} className="w-full">
                <Plus className="mr-1.5 h-4 w-4" /> Add step
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Metadata + submit */}
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="e.g. Phase III CSR intake"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="What this workflow does, when to use it, key assumptions."
                />
              </div>
              <div>
                <Label>Category</Label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as typeof type)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {WORKFLOW_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" disabled={create.isPending} className="w-full">
                <Save className="mr-1.5 h-4 w-4" />
                {create.isPending ? 'Saving...' : 'Save workflow'}
              </Button>
            </CardContent>
          </Card>

          <Card className={cn('border-primary/20 bg-primary/5')}>
            <CardContent className="p-4 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Design guidance</p>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                <li>Put human review <strong>after</strong> each high-risk AI step.</li>
                <li>Keep steps narrow — a single skill each. Chaining is clearer than mega-skills.</li>
                <li>Cross-reference steps catch extractions that contradict each other.</li>
                <li>Output generation should be the last step; it also does e-sig sealing.</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
